import { useState, type ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { enabledProviders, type ProviderId } from '../lib/firebase'
import styles from './SignInPage.module.css'

// handoff §4 / V4 "Sign in": federated identity only. Google, Microsoft,
// Apple, Meta in that order; which of them show is decided in lib/firebase
// (VITE_FIREBASE_MULTI_PROVIDER). Marks keep their official brand colours.

const PROVIDERS: Record<ProviderId, { name: string; mark: ReactNode }> = {
  google: {
    name: 'Google',
    mark: (
      <svg viewBox="0 0 24 24" width="19" height="19">
        <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.2-2.2H12v4.2h6.6c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.6-5 3.6-8.7Z" />
        <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.1 0-5.8-2.1-6.7-5l-3.8 3C3.5 21.3 7.4 24 12 24Z" />
        <path fill="#FBBC05" d="M5.3 14.4a7.4 7.4 0 0 1 0-4.8l-3.8-3a12 12 0 0 0 0 10.8l3.8-3Z" />
        <path fill="#EA4335" d="M12 4.7c2.2 0 3.7.95 4.5 1.75l3.3-3.2C17.9 1.2 15.2 0 12 0 7.4 0 3.5 2.7 1.5 6.6l3.8 3c.9-2.9 3.6-4.9 6.7-4.9Z" />
      </svg>
    ),
  },
  microsoft: {
    name: 'Microsoft',
    mark: (
      <svg viewBox="0 0 23 23" width="17" height="17">
        <path fill="#F25022" d="M0 0h11v11H0z" />
        <path fill="#7FBA00" d="M12 0h11v11H12z" />
        <path fill="#00A4EF" d="M0 12h11v11H0z" />
        <path fill="#FFB900" d="M12 12h11v11H12z" />
      </svg>
    ),
  },
  apple: {
    name: 'Apple',
    mark: (
      <svg viewBox="0 0 24 24" width="19" height="19">
        <path fill="#201e1d" d="M17.05 12.7c.03 3.2 2.8 4.26 2.83 4.28-.02.07-.45 1.53-1.48 3.03-.9 1.3-1.83 2.6-3.3 2.62-1.45.03-1.91-.86-3.57-.86-1.65 0-2.17.83-3.54.89-1.42.05-2.5-1.4-3.4-2.7-1.97-2.85-3.47-8.05-1.45-11.56 1-1.75 2.8-2.85 4.75-2.88 1.4-.03 2.72.94 3.57.94.85 0 2.46-1.16 4.14-.99.7.03 2.68.26 3.98 1.95-.1.06-2.36 1.38-2.33 4.1M14.4 3.9c.75-.9 1.25-2.16 1.11-3.41-1.1.04-2.44.73-3.21 1.63-.7.8-1.29 2.08-1.13 3.3 1.23.1 2.48-.62 3.23-1.52" />
      </svg>
    ),
  },
  meta: {
    name: 'Meta',
    mark: (
      <svg viewBox="0 0 24 24" width="20" height="20">
        <path fill="#0081FB" d="M6.9 15.6c.98 0 1.72-.6 3.32-3.4l.9-1.6c1.9-3.3 3.05-4.5 4.78-4.5 1.86 0 3.4 1.35 4.28 3.5.68 1.65.98 3.5.98 5.05 0 2.4-1.06 3.9-2.9 3.9v-2.2c.93 0 1.16-.77 1.16-1.9 0-1.36-.28-2.86-.83-4-.4-.82-.94-1.3-1.6-1.3-.72 0-1.32.55-2.5 2.6l-.83 1.47C11.7 17.2 10.4 19 8.5 19v-2.2c.94 0 1.65-.5 2.6-2.1" />
        <path fill="#0064E1" d="M6.66 8.35c-1.2 0-2.24 1-2.24 3.4 0 2.6 1.05 4 2.3 4 .78 0 1.4-.35 2.13-1.5l.6.98C8.4 17.13 7.6 19 6.5 19 3.9 19 2.2 16.35 2.2 11.9c0-4.1 2.02-6.3 4.63-6.3 1.6 0 2.87.86 4.06 2.8l-.87 1.4c-1-1.5-1.9-2.1-2.85-2.1" />
      </svg>
    ),
  },
}

const LAST_PROVIDER_KEY = 'dp-last-provider'

function readLastProvider(): string | null {
  try {
    return localStorage.getItem(LAST_PROVIDER_KEY)
  } catch {
    return null
  }
}

function rememberProvider(id: string) {
  try {
    localStorage.setItem(LAST_PROVIDER_KEY, id)
  } catch {
    // Storage can be unavailable (private window, blocked site data); the pill is a convenience.
  }
}

// An invitation link (pages/InvitePage) says what it is for above the buttons, in place of the welcome.
export function SignInPage({ title = 'Welcome back', lead, notice }: { title?: string; lead?: ReactNode; notice?: ReactNode } = {}) {
  const { signIn, error, status } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [lastProvider, setLastProvider] = useState(readLastProvider)

  const handleSignIn = async (id: ProviderId) => {
    setIsLoading(true)
    try {
      await signIn(id)
      rememberProvider(id)
      setLastProvider(id)
    } catch (err) {
      // Error is handled by auth context
      console.error('Sign-in failed:', err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.screen} data-screen-label="Sign in">
      <div className={styles.brandPanel}>
        <div className={`${styles.glow} ${styles.glowA}`} aria-hidden="true" />
        <div className={`${styles.glow} ${styles.glowB}`} aria-hidden="true" />
        <div className={`${styles.glow} ${styles.glowC}`} aria-hidden="true" />

        <div className={styles.wordmarkWrap}>
          <svg viewBox="0 0 453.14 147.96" width="196" className={styles.wordmark} role="img" aria-label="Dataphyll">
            <path d="M197.48,58.4h11.54c9.73,0,16.45,6.68,16.45,15.39v.09c0,8.71-6.72,15.48-16.45,15.48h-11.54v-30.96Zm5.44,4.95v21.05h6.1c6.5,0,10.75-4.38,10.75-10.44v-.09c0-6.06-4.25-10.53-10.75-10.53h-6.1Z" />
            <path d="M239.75,58.18h5.04l13.62,31.18h-5.75l-3.14-7.47h-14.64l-3.18,7.47h-5.57l13.62-31.18Zm7.78,18.89l-5.35-12.38-5.31,12.38h10.66Z" />
            <path d="M264.94,63.45h-9.82v-5.04h25.12v5.04h-9.82v25.92h-5.48v-25.92Z" />
            <path d="M291.2,58.18h5.04l13.62,31.18h-5.75l-3.14-7.47h-14.64l-3.18,7.47h-5.57l13.62-31.18Zm7.78,18.89l-5.35-12.38-5.31,12.38h10.66Z" />
            <path d="M312.56,58.4h12.21c7.25,0,11.77,4.11,11.77,10.35v.09c0,6.94-5.57,10.57-12.38,10.57h-6.15v9.95h-5.44v-30.96Zm11.76,16.1c4.11,0,6.68-2.3,6.68-5.53v-.09c0-3.63-2.61-5.53-6.68-5.53h-6.32v11.14h6.32Z" />
            <path d="M339.5,58.4h5.44v12.87h14.77v-12.87h5.44v30.96h-5.44v-13.05h-14.77v13.05h-5.44v-30.96Z" />
            <path d="M379.61,77.16l-12.29-18.75h6.46l8.63,13.67,8.76-13.67h6.24l-12.3,18.62v12.34h-5.48v-12.21Z" />
            <path d="M399.56,58.4h5.44v26.01h16.28v4.95h-21.72v-30.96Z" />
            <path d="M424.33,58.4h5.44v26.01h16.28v4.95h-21.72v-30.96Z" />
            <path d="M74.49,2.91H3.63V117.08c17.9-1.59,44.14-9.49,62.73-28.46,4.33-4.29,7.18-7.62,10.22-13.01h-.73c-10.3,14.59-25.38,22.47-39.38,28.21-2.99-9.33-6.99-27.06,9.82-41.79,5.49-4.82,10.99-6.82,16.31-8.76,7.78-2.84,15.82-5.78,25.38-17.73l1.5-1.87,1.67,1.72c16.13,16.68,17.97,46.17,3.86,61.88-12.7,14.14-29.67,18.09-41.66,18.09-4.36,0-9.3-.98-11.77-1.86-12.26,5.94-24.93,10.14-37.94,11.86v19.28H74.49c39.14,0,70.87-31.73,70.87-70.87S113.63,2.91,74.49,2.91Z" />
          </svg>
        </div>

        <div className={styles.brandCopy}>
          <div className={styles.product}>CONTROL</div>
          <p className={styles.strapline}>
            Workforce, timesheets, wages and yield reporting for the whole operation. One place for the office.
          </p>
        </div>

        <div className={styles.copyright}>© 2026 Dataphyll</div>
      </div>

      <main className={styles.signInPanel}>
        <div className={styles.content}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.lead}>{lead ?? 'Continue with the account your workplace already uses. No passwords to keep.'}</p>
          {notice}

          <div className={styles.card}>
            {enabledProviders.map((id, i) => (
              <button
                key={id}
                type="button"
                onClick={() => handleSignIn(id)}
                disabled={isLoading || status === 'loading'}
                className={styles.providerButton}
                style={{ animationDelay: `${0.42 + i * 0.06}s` }}
              >
                <span className={styles.providerMark} aria-hidden="true">
                  {PROVIDERS[id].mark}
                </span>
                <span className={styles.providerName}>Continue with {PROVIDERS[id].name}</span>
                {lastProvider === id && <span className={styles.lastUsed}>Last used</span>}
              </button>
            ))}

            {error && (
              <div className={styles.error} role="alert">
                {error.message}
              </div>
            )}

            {status === 'loading' && <div className={styles.loading}>Signing in...</div>}

            <div className={styles.accessNote}>
              Access is arranged by your Dataphyll administrator. If your account has no access, ask them to add you.
              Sign-in cannot request it for you.
            </div>
          </div>

          <div className={styles.goodPanel}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" className={styles.goodIcon} aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>No password to remember or reset. Your access is controlled in CONTROL, whichever account you sign in with.</span>
          </div>

          <p className={styles.support}>
            Trouble signing in? <a href="mailto:support@dataphyll.com">support@dataphyll.com</a>
          </p>
        </div>
      </main>
    </div>
  )
}
